import { useEffect, useState, Fragment, useRef, useCallback } from "react";

import { API_BASE_URL } from "../utils/config";
import CustomSelect from "../components/CustomSelect";
const authHeaders = () => {
  const token = localStorage.getItem("accessToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
};


// ----------------- IMAGE HELPERS -----------------

function normalizeImageUrl(url) {
  if (!url) return null;
  const trimmed = url.trim();
  if (!trimmed) return null;

  // Helper: extract Google Drive file ID from many formats
  const extractDriveId = (value) => {
    if (!value) return null;

    // Full URL?
    if (/^https?:\/\//i.test(value)) {
      if (value.includes("drive.google.com")) {
        try {
          // Pattern: https://drive.google.com/file/d/FILE_ID/view?usp=sharing
          const match = value.match(/\/file\/d\/([^/]+)/);
          if (match && match[1]) {
            return match[1];
          }
          // Pattern: https://drive.google.com/open?id=FILE_ID
          const urlObj = new URL(value);
          const idParam = urlObj.searchParams.get("id");
          if (idParam) return idParam;
        } catch (e) {
          return null;
        }
      }
      return null;
    }

    // uc?export=view&id=FILE_ID or /uc?export=view&id=FILE_ID
    if (value.startsWith("uc?") || value.startsWith("/uc?")) {
      const search = value.startsWith("/") ? value.slice(1) : value;
      const parts = search.split("?");
      const query = parts.length > 1 ? parts[1] : parts[0];
      const params = new URLSearchParams(query);
      const id = params.get("id");
      if (id) return id;
    }

    // id=FILE_ID
    if (value.startsWith("id=")) {
      return value.substring(3).split("&")[0];
    }

    // Plain FILE_ID
    if (/^[A-Za-z0-9_-]{20,}$/.test(value)) {
      return value;
    }

    return null;
  };

  const driveId = extractDriveId(trimmed);
  if (driveId) {
    // IMPORTANT: use backend proxy for preview <img>
    return `${API_BASE_URL}/api/utils/drive-image/?id=${encodeURIComponent(
      driveId
    )}`;
  }

  // Non-Drive full URL → use as is
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  // Anything else (like "0") we just return as-is
  return trimmed;
}

function buildExternalImageUrl(raw) {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  // Already full URL
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  // uc?export=view&id=FILE_ID or /uc?...
  if (trimmed.startsWith("uc?") || trimmed.startsWith("/uc?")) {
    const search = trimmed.startsWith("/") ? trimmed.slice(1) : trimmed;
    const parts = search.split("?");
    const query = parts.length > 1 ? parts[1] : parts[0];
    const params = new URLSearchParams(query);
    const id = params.get("id");
    if (id) {
      return `https://drive.google.com/uc?export=view&id=${id}`;
    }
  }

  // id=FILE_ID
  if (trimmed.startsWith("id=")) {
    const id = trimmed.substring(3).split("&")[0];
    return `https://drive.google.com/uc?export=view&id=${id}`;
  }

  // Plain Drive ID
  if (/^[A-Za-z0-9_-]{20,}$/.test(trimmed)) {
    return `https://drive.google.com/uc?export=view&id=${trimmed}`;
  }

  // Things like "0" → invalid external URL
  return null;
}

function ItemsList() {
  // list + loading
  const [user, setUser] = useState(null);
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // search + status filter
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");

  // pagination
  const [page, setPage] = useState(1);
  const pageSize = 25;

  // category + location master
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(true);

  // add item form
  const [showForm, setShowForm] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [unit, setUnit] = useState("");
  const [posisi, setPosisi] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [initialQuantity, setInitialQuantity] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");

  // history modal
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyItem, setHistoryItem] = useState(null);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");

  // delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  // image preview modal
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [previewRawUrl, setPreviewRawUrl] = useState(null);
  const [previewError, setPreviewError] = useState(false);
  const [loadingImages, setLoadingImages] = useState({});

  const externalUrl = buildExternalImageUrl(previewRawUrl);

  const [sortField, setSortField] = useState("code");
  const [sortDir, setSortDir] = useState("asc");

  // Force previewError if image never loads (CORB / weird cases)
  useEffect(() => {
    if (!previewImageUrl) return;

    setPreviewError(false);

    const timeout = setTimeout(() => {
      setPreviewError(true);
    }, 1500); // 1.5s is enough for good images to load

    return () => clearTimeout(timeout);
  }, [previewImageUrl]);

  const [expandedItemId, setExpandedItemId] = useState(null);

  // edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editName, setEditName] = useState("");
  const [editCategoryId, setEditCategoryId] = useState("");
  const [editLocationId, setEditLocationId] = useState("");
  const [editUnit, setEditUnit] = useState("");
  const [editPosisi, setEditPosisi] = useState("");
  const [editMinimumStock, setEditMinimumStock] = useState(0);
  const [editHighPriority, setEditHighPriority] = useState(false);
  const [editImageUrl, setEditImageUrl] = useState("");
  const [editError, setEditError] = useState("");
  const [editLoading, setEditLoading] = useState(false);

  // photo upload (add form)
  const [addPhotoFile, setAddPhotoFile] = useState(null);
  const [addPhotoPreview, setAddPhotoPreview] = useState(null);
  const [addPhotoUploading, setAddPhotoUploading] = useState(false);
  const addPhotoInputRef = useRef(null);

  // photo upload (edit modal)
  const [editPhotoFile, setEditPhotoFile] = useState(null);
  const [editPhotoPreview, setEditPhotoPreview] = useState(null);
  const [editPhotoUploading, setEditPhotoUploading] = useState(false);
  const [editCurrentPhotoUrl, setEditCurrentPhotoUrl] = useState(null);
  const editPhotoInputRef = useRef(null);

  const requestIdRef = useRef(0);

  const fetchNextCode = () => {
    fetch(`${API_BASE_URL}/api/items/next-code/`, { headers: { ...authHeaders() } })
      .then((r) => r.json())
      .then((data) => { if (data.next_code) setCode(data.next_code); })
      .catch(() => {});
  };

  const fetchItems = () => {
    setLoading(true);

    // bump request id
    const currentRequestId = ++requestIdRef.current;

    const params = new URLSearchParams();
    params.set("page", page);
    params.set("page_size", pageSize);

    const term = search.trim();
    if (term !== "") {
      params.set("search", term);
    }

    const effectiveStatus = isAdmin ? statusFilter : "active";
    params.set("status", effectiveStatus);

    const orderingParam = `${sortDir === "desc" ? "-" : ""}${sortField}`;
    params.set("ordering", orderingParam);

    params.set("temporary_only", "false");
    fetch(`${API_BASE_URL}/api/items/?${params.toString()}`, {
      headers: { ...authHeaders() },
    })
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json().catch(() => null);
          throw new Error(data?.detail || "Failed to load items.");
        }
        return res.json();
      })
      .then((data) => {
        // ignore if this is not the latest request
        if (currentRequestId !== requestIdRef.current) return;

        const results = Array.isArray(data) ? data : data.results || [];
        setItems(results);

        if (!Array.isArray(data) && typeof data === "object" && typeof data.count === "number") {
          setTotalCount(data.count);
        } else {
          setTotalCount(results.length);
        }
      })
      .catch((err) => {
        if (currentRequestId !== requestIdRef.current) return;
        console.error(err);
      })
      .finally(() => {
        if (currentRequestId !== requestIdRef.current) return;
        setLoading(false);
      });
  };

  // --- fetch logged-in user (role) like Sidebar ---
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;

    fetch(`${API_BASE_URL}/api/auth/me/`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then((data) => setUser(data))
      .catch(() => setUser(null));
  }, []);


  const isAdmin = ["admin", "superadmin"].includes(user?.role);
  const isGuest = user && !["admin", "superadmin"].includes(user.role);

  useEffect(() => {
    if (isGuest && statusFilter !== "active") {
      setStatusFilter("active");
    }
  }, [isGuest, statusFilter]);


  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, statusFilter, sortField, sortDir]);

  useEffect(() => {
    setLoadingCategories(true);
    fetch(`${API_BASE_URL}/api/categories/`, {
      headers: { ...authHeaders() },
    })
      .then((res) => res.json())
      .then((data) => {
        setCategories(Array.isArray(data) ? data : data.results || []);
      })
      .catch((err) => {
        console.error("Error fetching categories:", err);
      })
      .finally(() => {
        setLoadingCategories(false);
      });
  }, []);

  useEffect(() => {
    setLoadingLocations(true);
    fetch(`${API_BASE_URL}/api/locations/`, {
      headers: { ...authHeaders() },
    })
      .then((res) => res.json())
      .then((data) => {
        setLocations(Array.isArray(data) ? data : data.results || []);
      })
      .catch((err) => {
        console.error("Error fetching locations:", err);
      })
      .finally(() => {
        setLoadingLocations(false);
      });
  }, []);

  const resetForm = () => {
    setCode("");
    setName("");
    setCategoryId("");
    setLocationId("");
    setUnit("");
    setPosisi("");
    setImageUrl("");
    setInitialQuantity("");
    setFormError("");
    setFormSuccess("");
    setAddPhotoFile(null);
    setAddPhotoPreview(null);
    if (addPhotoInputRef.current) addPhotoInputRef.current.value = "";
  };

  const getTotalIn = (item) => {
    const total = item.total_in ?? item.quantity_in ?? item.masuk;
    if (total !== undefined && total !== null) {
      return total;
    }
    return item.quantity_available ?? item.tersedia ?? item.available ?? 0;
  };

  const getTotalOut = (item) =>
    item.total_out ?? item.quantity_out ?? item.keluar ?? 0;

  const handleToggleLocations = (itemId) => {
    setExpandedItemId((prev) => (prev === itemId ? null : itemId));
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const pagedItems = items;

  const toggleSort = (field) => {
    setPage(1);
    if (sortField !== field) {
      setSortField(field);
      setSortDir("asc");
      return;
    }
    setSortDir((d) => (d === "asc" ? "desc" : "asc"));
  };

  const sortIcon = (field) => {
    if (sortField !== field) return "";
    return sortDir === "asc" ? " ↑" : " ↓";
  };

  // ---------- create item ----------

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError("");
    setFormSuccess("");

    if (!code.trim()) {
      setFormError("Please enter an item code.");
      return;
    }
    if (!name.trim()) {
      setFormError("Please enter an item name.");
      return;
    }
    if (!categoryId) {
      setFormError("Please select a category.");
      return;
    }
    if (!locationId) {
      setFormError("Please select a location.");
      return;
    }

    let initialQty = 0;
    if (initialQuantity !== "") {
      const num = Number(initialQuantity);
      if (Number.isNaN(num) || num < 0) {
        setFormError("Available stock must be 0 or a positive number.");
        return;
      }
      initialQty = num;
    }

    const payload = {
      code: code.trim(),
      name: name.trim(),
      unit: unit.trim() || null,
      posisi: posisi.trim() || "",
      category_id: Number(categoryId),
      location_id: Number(locationId),
    };

    if (imageUrl.trim() !== "") {
      payload.image_url = imageUrl.trim();
    }

    if (initialQty > 0) {
      payload.initial_quantity = initialQty;
    }

    setSubmitting(true);

    fetch(`${API_BASE_URL}/api/items/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders(),
      },
      body: JSON.stringify(payload),
    })

      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json().catch(() => null);
          let msg = "Failed to create item.";
          if (data && typeof data === "object") {
            const firstKey = Object.keys(data)[0];
            if (firstKey && Array.isArray(data[firstKey])) {
              msg = data[firstKey][0];
            } else if (data.detail) {
              msg = data.detail;
            }
          }
          throw new Error(msg);
        }
        return res.json();
      })
      .then(async (createdItem) => {
        if (addPhotoFile && createdItem?.id) {
          setAddPhotoUploading(true);
          const fd = new FormData();
          fd.append("image", addPhotoFile);
          try {
            await fetch(`${API_BASE_URL}/api/items/${createdItem.id}/photo/`, {
              method: "POST",
              headers: { ...authHeaders() },
              body: fd,
            });
          } catch (_) {}
          setAddPhotoUploading(false);
        }
        setFormSuccess("Item created successfully.");
        resetForm();
        // reload list from first page
        setPage(1);
        fetchItems();
      })
      .catch((err) => {
        console.error(err);
        setFormError(err.message || "Failed to create item.");
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  // ---------- delete item ----------

  const handleConfirmDelete = () => {
    if (!deleteItemId) {
      setDeleteError("Please select an item to remove.");
      return;
    }

    setDeleteError("");
    setDeleteLoading(true);

    fetch(`${API_BASE_URL}/api/items/${deleteItemId}/`, {
      method: "DELETE",
      headers: (() => {
        const h = {};
        const t = localStorage.getItem("accessToken");
        if (t) h["Authorization"] = `Bearer ${t}`;
        return h;
      })(),
    })
      .then(async (res) => {
        if (res.status === 204) return;
        if (!res.ok) {
          const data = await res.json().catch(() => null);
          let msg = "Failed to remove item.";
          if (data && typeof data === "object" && data.detail) {
            msg = data.detail;
          }
          throw new Error(msg);
        }
      })
      .then(() => {
        setItems((prev) =>
          prev.filter((it) => String(it.id) !== String(deleteItemId))
        );
        setShowDeleteModal(false);
        setDeleteItemId("");
      })
      .catch((err) => {
        console.error(err);
        setDeleteError(err.message || "Failed to remove item.");
      })
      .finally(() => {
        setDeleteLoading(false);
      });
  };

  // ---------- edit item ----------

  const openEditModal = (item) => {
    setEditItem(item);
    setEditName(item.name || "");
    setEditCategoryId(item.category?.id ? String(item.category.id) : "");
    setEditLocationId(item.location?.id ? String(item.location.id) : "");
    setEditUnit(item.unit || "");
    setEditPosisi(item.posisi || "");
    setEditMinimumStock(item.minimum_stock ?? 0);
    setEditHighPriority(!!item.is_high_priority);
    const rawImg = item.image_url || item.gambar || "";
    setEditImageUrl(rawImg || "");
    setEditCurrentPhotoUrl(item.image_file_url || null);
    setEditPhotoFile(null);
    setEditPhotoPreview(null);
    setEditError("");
    setEditLoading(false);
    setShowEditModal(true);
  };

  const openHistoryModal = (item) => {
    setHistoryItem(item);
    setShowHistoryModal(true);
    setHistoryEntries([]);
    setHistoryError("");
    setHistoryLoading(true);
    fetch(`${API_BASE_URL}/api/items/${item.id}/history/`, { headers: authHeaders() })
      .then((r) => {
        if (!r.ok) throw new Error("Gagal memuat riwayat.");
        return r.json();
      })
      .then((data) => setHistoryEntries(Array.isArray(data) ? data : []))
      .catch(() => setHistoryError("Gagal memuat riwayat."))
      .finally(() => setHistoryLoading(false));
  };

  const closeHistoryModal = () => {
    setShowHistoryModal(false);
    setHistoryItem(null);
    setHistoryEntries([]);
    setHistoryError("");
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditItem(null);
    setEditName("");
    setEditCategoryId("");
    setEditLocationId("");
    setEditUnit("");
    setEditPosisi("");
    setEditMinimumStock(0);
    setEditHighPriority(false);
    setEditImageUrl("");
    setEditCurrentPhotoUrl(null);
    setEditPhotoFile(null);
    setEditPhotoPreview(null);
    setEditError("");
    setEditLoading(false);
  };

  const handleEditPhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setEditPhotoFile(file);
    setEditPhotoPreview(URL.createObjectURL(file));
  };

  const handleUploadPhoto = useCallback(async () => {
    if (!editPhotoFile || !editItem) return;
    setEditPhotoUploading(true);
    const formData = new FormData();
    formData.append("image", editPhotoFile);
    try {
      const res = await fetch(`${API_BASE_URL}/api/items/${editItem.id}/photo/`, {
        method: "POST",
        headers: { ...authHeaders() },
        body: formData,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.detail || `Upload gagal (${res.status}).`);
      }
      const data = await res.json();
      setEditCurrentPhotoUrl(data.image_file_url);
      setEditPhotoFile(null);
      setEditPhotoPreview(null);
      if (editPhotoInputRef.current) editPhotoInputRef.current.value = "";
      setItems((prev) =>
        prev.map((it) =>
          it.id === editItem.id ? { ...it, image_file_url: data.image_file_url } : it
        )
      );
    } catch (err) {
      setEditError(err.message);
    } finally {
      setEditPhotoUploading(false);
    }
  }, [editPhotoFile, editItem]);

  const handleRemovePhoto = useCallback(async () => {
    if (!editItem) return;
    if (!window.confirm("Hapus foto ini?")) return;
    try {
      await fetch(`${API_BASE_URL}/api/items/${editItem.id}/photo/`, {
        method: "DELETE",
        headers: { ...authHeaders() },
      });
      setEditCurrentPhotoUrl(null);
      setItems((prev) =>
        prev.map((it) =>
          it.id === editItem.id ? { ...it, image_file_url: null } : it
        )
      );
    } catch (err) {
      setEditError("Gagal menghapus foto.");
    }
  }, [editItem]);


  const handleHideItem = () => {
    if (!editItem) return;
    if (!window.confirm("Hide this item? It will be removed from the active list.")) return;

    fetch(`${API_BASE_URL}/api/items/${editItem.id}/`, {
      method: "DELETE",
      headers: {
        ...authHeaders(),
      },
    })

      .then((res) => {
        if (!res.ok) throw new Error("Failed to hide item.");
        // Remove from current list
        setItems((prev) => prev.filter((it) => it.id !== editItem.id));
        closeEditModal();
      })
      .catch((err) => {
        console.error(err);
        setEditError("Failed to hide item.");
      });
  };

  const handleRestoreItem = () => {
    if (!editItem) return;

    const payload = { ...editItem, is_active: true };

    fetch(`${API_BASE_URL}/api/items/${editItem.id}/`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders(),
      },
      body: JSON.stringify(payload),
    })

      .then((res) => res.json())
      .then((updated) => {
        // update item in list
        setItems((prev) =>
          prev.map((it) => (it.id === updated.id ? updated : it))
        );
        closeEditModal();
      })
      .catch((err) => {
        console.error(err);
        setEditError("Failed to restore item.");
      });
  };

  const handleUpdateItem = (e) => {
    e.preventDefault();
    if (!editItem) return;

    setEditError("");

    if (!editItem.code.trim()) {
      setEditError("Item code is required.");
      return;
    }
    if (!editName.trim()) {
      setEditError("Item name is required.");
      return;
    }
    if (!editCategoryId) {
      setEditError("Category is required.");
      return;
    }
    if (!editLocationId) {
      setEditError("Location is required.");
      return;
    }

    const payload = {
      code: (editItem.code || "").trim(),
      name: editName.trim(),
      unit: editUnit.trim() || null,
      posisi: editPosisi.trim() || "",
      category_id: Number(editCategoryId),
      location_id: Number(editLocationId),
      minimum_stock: Number(editMinimumStock) || 0,
      is_high_priority: editHighPriority,
    };

    const img = editImageUrl.trim();
    if (img !== "") {
      payload.image_url = img;
    } else {
      payload.image_url = null;
    }

    setEditLoading(true);

    // prepare headers (include Authorization if token present)
    const updateHeaders = { "Content-Type": "application/json" };
    const updateToken = localStorage.getItem("accessToken");
    if (updateToken) updateHeaders["Authorization"] = `Bearer ${updateToken}`;

    fetch(`${API_BASE_URL}/api/items/${editItem.id}/`, {
      method: "PUT",
      headers: updateHeaders,
      body: JSON.stringify(payload),
    })
      .then(async (res) => {
        const data = await res.json().catch(() => null);
        if (!res.ok) {
          let msg = "Failed to update item.";
          if (data && typeof data === "object") {
            const firstKey = Object.keys(data)[0];
            if (firstKey && Array.isArray(data[firstKey])) {
              msg = `${firstKey}: ${data[firstKey][0]}`;
            } else if (data.detail) {
              msg = data.detail;
            }
          }
          throw new Error(msg);
        }
        return data;
      })
      .then((updated) => {
        setItems((prev) =>
          prev.map((it) => (it.id === updated.id ? updated : it))
        );
        closeEditModal();
      })
      .catch((err) => {
        console.error(err);
        setEditError(err.message || "Failed to update item.");
        setEditLoading(false);
      });
  };

  const markImageLoading = (itemId) => {
    setLoadingImages((prev) => ({ ...prev, [itemId]: true }));
  };

  const markImageDone = (itemId) => {
    setLoadingImages((prev) => ({ ...prev, [itemId]: false }));
  };

  return (
    <div className="pt-2">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-strong">
          Barang
        </h1>
        <p className="text-sm text-muted mt-1">
          {totalCount} item{totalCount === 1 ? "" : "s"} in your inventory
        </p>
      </div>

      {/* Top bar: floating search + toggle add form */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
        <div className="flex-1 flex items-center gap-2">
          <div className="relative flex-1 md:max-w-md">
            <svg
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={1.8}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setPage(1);
                setSearch(e.target.value);
              }}
              placeholder="Search by code, name, category, location…"
              className="glass-input pl-10"
            />
          </div>

          {/* status filter */}
          {isAdmin && (
            <CustomSelect
              value={statusFilter}
              onChange={(v) => { setPage(1); setStatusFilter(v); }}
              className="w-40"
              options={[
                { value: "active", label: "Active items" },
                { value: "hidden", label: "Hidden items" },
                { value: "all",    label: "All items"    },
              ]}
            />
          )}
        </div>
        {isAdmin && (
          <button
            type="button"
            onClick={() => {
              const opening = !showForm;
              setShowForm(opening);
              if (opening) fetchNextCode();
            }}
            className="btn-primary"
          >
            {showForm ? "Hide form" : "+ Add item"}
          </button>
        )}
      </div>

      {/* Add item form */}
      {showForm && (
        <div className="glass-card p-5 mb-5 animate-scale-in">
          <h2 className="text-base font-semibold text-strong mb-4">Add New Item</h2>

          {formError && (
            <div className="mb-2 text-xs text-red-600">{formError}</div>
          )}
          {formSuccess && (
            <div className="mb-2 text-xs text-green-600">{formSuccess}</div>
          )}

          <form
            onSubmit={handleSubmit}
            className="grid gap-3 md:grid-cols-2 lg:grid-cols-3"
          >
            {/* Code */}
            <div>
              <label className="block text-xs font-medium text-muted mb-1.5">
                Item Code * <span className="font-normal text-muted">(auto-generated, bisa diubah)</span>
              </label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="glass-input"
                placeholder="e.g. ITM-0001"
              />
            </div>

            {/* Name */}
            <div>
              <label className="block text-xs font-medium text-muted mb-1.5">
                Item Name *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="glass-input"
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-xs font-medium text-muted mb-1.5">
                Category *
              </label>
              <CustomSelect
                value={categoryId}
                onChange={setCategoryId}
                placeholder={loadingCategories ? "Loading categories…" : "Select category"}
                options={categories.map((cat) => ({ value: String(cat.id), label: cat.name }))}
              />
            </div>

            {/* Location */}
            <div>
              <label className="block text-xs font-medium text-muted mb-1.5">
                Location *
              </label>
              <CustomSelect
                value={locationId}
                onChange={setLocationId}
                placeholder={loadingLocations ? "Loading locations…" : "Select location"}
                options={locations.map((loc) => ({ value: String(loc.id), label: loc.name }))}
              />
            </div>

            {/* Unit */}
            <div>
              <label className="block text-xs font-medium text-muted mb-1.5">
                Unit
              </label>
              <input
                type="text"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                className="glass-input"
                placeholder="e.g. pcs, set, unit"
              />
            </div>

            {/* Posisi */}
            <div>
              <label className="block text-xs font-medium text-muted mb-1.5">
                Posisi
              </label>
              <input
                type="text"
                value={posisi}
                onChange={(e) => setPosisi(e.target.value)}
                className="glass-input"
                placeholder="e.g. Rak A1 / Zona 3"
              />
            </div>

            {/* Initial stock */}
            <div>
              <label className="block text-xs font-medium text-muted mb-1.5">
                Available stock
              </label>
              <input
                type="number"
                min="0"
                value={initialQuantity}
                onChange={(e) => setInitialQuantity(e.target.value)}
                className="glass-input"
                placeholder="e.g. 10"
              />
            </div>

            {/* Foto Barang (upload / kamera) */}
            <div className="md:col-span-2 lg:col-span-2">
              <label className="block text-xs font-medium text-muted mb-1.5">
                Foto Barang
              </label>
              {addPhotoPreview && (
                <div className="mb-3 flex items-start gap-3">
                  <img
                    src={addPhotoPreview}
                    alt="Preview"
                    className="w-24 h-24 object-cover rounded-xl border border-white/20"
                  />
                  <button
                    type="button"
                    onClick={() => { setAddPhotoFile(null); setAddPhotoPreview(null); if (addPhotoInputRef.current) addPhotoInputRef.current.value = ""; }}
                    className="btn-ghost !px-3 !py-1.5 !text-xs mt-1"
                  >
                    Hapus
                  </button>
                </div>
              )}
              <div className="flex gap-2 flex-wrap">
                <label className="btn-outline !px-3 !py-1.5 !text-xs cursor-pointer">
                  <input
                    ref={addPhotoInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setAddPhotoFile(file);
                      setAddPhotoPreview(URL.createObjectURL(file));
                    }}
                  />
                  Upload Foto
                </label>
                <label className="btn-outline !px-3 !py-1.5 !text-xs cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setAddPhotoFile(file);
                      setAddPhotoPreview(URL.createObjectURL(file));
                    }}
                  />
                  Ambil Foto (Kamera)
                </label>
              </div>
              <p className="mt-1.5 text-[11px] text-muted">
                Upload atau foto dari kamera HP. Format: JPG, PNG, WEBP.
              </p>
            </div>

            {/* Image URL */}
            <div className="md:col-span-2 lg:col-span-2">
              <label className="block text-xs font-medium text-muted mb-1.5">
                Image URL
              </label>
              <input
                type="text"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                className="glass-input"
                placeholder="https://example.com/image.jpg"
              />
              <p className="mt-1 text-[11px] text-muted">
                Optional. Paste a direct image link or Google Drive file link.
              </p>
            </div>

            <div className="md:col-span-2 lg:col-span-3 flex justify-end mt-1">
              <button
                type="submit"
                disabled={submitting || addPhotoUploading}
                className="btn-primary"
              >
                {addPhotoUploading ? "Uploading foto…" : submitting ? "Saving…" : "Save Item"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Items table */}
      <div className="glass-table-wrap overflow-x-auto">
        <table className="glass-table">
          <thead>
            <tr>
              <th className="text-left">
                <button
                  type="button"
                  onClick={() => toggleSort("code")}
                  className="hover:text-strong transition-colors"
                >
                  Code{sortIcon("code")}
                </button>
              </th>

              <th className="text-left">
                <button
                  type="button"
                  onClick={() => toggleSort("name")}
                  className="hover:text-strong transition-colors"
                >
                  Name{sortIcon("name")}
                </button>
              </th>

              <th className="text-left">
                <button
                  type="button"
                  onClick={() => toggleSort("category__name")}
                  className="hover:text-strong transition-colors"
                >
                  Category{sortIcon("category__name")}
                </button>
              </th>

              <th className="text-left">
                Location
              </th>
              <th className="text-left">
                Unit
              </th>
              <th className="text-left">
                Posisi
              </th>
              <th className="!text-right">
                Stock
              </th>
              <th className="!text-right">
                Keluar
              </th>
              <th className="!text-right">
                Tersedia
              </th>
              <th className="!text-right">
                Dipesan
              </th>
              <th className="!text-center">
                Image
              </th>
              <th className="!text-center">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td
                  colSpan={12}
                  className="px-4 py-8 text-center text-muted"
                >
                  Loading items...
                </td>
              </tr>
            )}

            {!loading &&
              pagedItems.map((item) => {
                const totalIn = getTotalIn(item);
                const availableFromLocations = (item.location_stocks || []).reduce(
                  (sum, ls) => sum + (Number(ls.available) || 0),
                  0
                );
                const available = availableFromLocations;
                let totalOut = getTotalOut(item);
                if (totalIn !== undefined && totalIn !== null) {
                  totalOut = Math.max(0, Number(totalIn) - Number(available));
                }

                const displayStock = totalIn ?? available;
                const displayAvailable = Math.max(0, Number(available) - Number(item.quantity_pending || 0));

                const rawImg = item.image_url || item.gambar || null;
                const resolveUrl = (u) => u && u.startsWith("/") ? `${API_BASE_URL}${u}` : u;
                const img = resolveUrl(item.image_file_url) || normalizeImageUrl(rawImg);

                if (img && loadingImages[item.id] === undefined) {
                  markImageLoading(item.id);
                }


                return (
                  <Fragment key={item.id}>
                    {/* main row – columns stay in the same order */}
                    <tr className="border-t">
                      <td className="px-4 py-2 font-semibold">{item.code}</td>
                      <td className="px-4 py-2">{item.name}</td>
                      <td className="px-4 py-2">
                        {item.category?.name || "-"}
                      </td>

                      {/* LOCATION: clickable to expand / collapse */}
                      <td className="px-4 py-2">
                        {item.location_stocks && item.location_stocks.length > 0 ? (
                          <button
                            type="button"
                            onClick={() => handleToggleLocations(item.id)}
                            className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 transition-colors"
                          >
                            <span>
                              {expandedItemId === item.id
                                ? "Hide locations"
                                : "View locations"}
                            </span>
                            <span className="text-[10px] text-muted">
                              ({item.location_stocks.length})
                            </span>
                          </button>
                        ) : item.location?.name ? (
                          <span className="text-xs text-muted">{item.location.name}</span>
                        ) : (
                          <span className="text-xs text-muted">-</span>
                        )}
                      </td>

                      <td className="px-4 py-2 text-xs text-muted">
                        {item.unit || "-"}
                      </td>
                      <td className="px-4 py-2 text-xs text-muted">
                        {item.posisi || "-"}
                      </td>
                      <td className="px-4 py-2 text-right">{displayStock}</td>
                      <td className="px-4 py-2 text-right">{totalOut}</td>
                      <td className="px-4 py-2 text-right font-semibold">
                        {displayAvailable}
                      </td>
                      <td className="px-4 py-2 text-right text-muted">
                        {Number(item.quantity_pending) > 0 ? item.quantity_pending : "-"}
                      </td>

                      {/* IMAGE column */}
                      <td className="px-4 py-2 text-center">
                        {img ? (
                          <button
                            type="button"
                            onClick={() => {
                              setPreviewImageUrl(img);   // proxied / normalized URL
                              setPreviewRawUrl(rawImg);  // original Drive link or whatever they typed
                              setPreviewError(false);
                            }}
                            className="inline-block"
                          >
                            <div className="relative inline-block h-10 w-10">
                              {loadingImages[item.id] !== false && (
                                <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded border border-white/40 dark:border-white/10">
                                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/50 dark:border-white/15 border-t-brand-500" />
                                </div>
                              )}

                              <img
                                src={img}
                                alt={item.name}
                                onLoad={() => markImageDone(item.id)}
                                onError={() => markImageDone(item.id)}
                                onLoadStart={() => markImageLoading(item.id)}
                                className="h-10 w-10 rounded object-cover border border-white/40 dark:border-white/10 hover:opacity-80 transition"
                              />
                            </div>

                          </button>
                        ) : (
                          <div className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/5 dark:bg-white/10 text-[10px] text-muted border border-white/40 dark:border-white/10">
                            N/A
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-center relative">
                        <div className="inline-flex gap-1.5">
                          <button
                            type="button"
                            onClick={() => openEditModal(item)}
                            className="btn-ghost !px-3 !py-1.5 !text-xs"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => openHistoryModal(item)}
                            className="btn-ghost !px-3 !py-1.5 !text-xs"
                          >
                            Riwayat
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* EXPANDED ROW: stock per location */}
                    {expandedItemId === item.id && (
                      <tr className="bg-brand-500/5">
                        <td colSpan={11} className="px-8 py-3">
                          <div className="text-xs text-strong">
                            <table className="min-w-[320px] text-xs">
                              <thead>
                                <tr className="text-left text-muted">
                                  <th className="py-1 pr-4">Location</th>
                                  <th className="py-1 pr-4">Posisi</th>
                                  <th className="py-1 pr-4 text-right">
                                    Available
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {item.location_stocks.map((ls) => (
                                  <tr
                                    key={ls.location?.id || ls.location_name}
                                  >
                                    <td className="py-0.5 pr-4">
                                      {ls.location?.name ||
                                        ls.location_name ||
                                        "-"}
                                    </td>
                                    <td className="py-0.5 pr-4">
                                      {ls.posisi || item.posisi || "-"}
                                    </td>
                                    <td className="py-0.5 pr-4 text-right">
                                      {ls.available ?? ls.tersedia ?? 0}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* Pagination controls */}
      <div className="flex flex-wrap justify-between items-center gap-3 mt-5">
        <div className="flex items-center gap-2">
          <button
            className="btn-ghost !px-3 !py-1.5 !text-xs disabled:opacity-40"
            disabled={page === 1}
            onClick={() => setPage(1)}
          >
            « First
          </button>

          <button
            className="btn-ghost !px-3 !py-1.5 !text-xs disabled:opacity-40"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            ‹ Prev
          </button>
        </div>

        <span className="text-sm text-muted">
          Page <span className="font-semibold text-strong">{page}</span> of{" "}
          {totalPages}
        </span>

        <div className="flex items-center gap-2">
          <button
            className="btn-ghost !px-3 !py-1.5 !text-xs disabled:opacity-40"
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next ›
          </button>

          <button
            className="btn-ghost !px-3 !py-1.5 !text-xs disabled:opacity-40"
            disabled={page === totalPages}
            onClick={() => setPage(totalPages)}
          >
            Last »
          </button>
        </div>
      </div>

      {/* Image preview modal */}
      {previewImageUrl && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-fade-in"
          onClick={() => {
            setPreviewImageUrl(null);
            setPreviewRawUrl(null);
            setPreviewError(false);
          }}
        >
          <div
            className="glass-card max-w-3xl w-full p-5 animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-sm font-semibold text-strong">Item image</h2>
              <button
                type="button"
                className="text-xs text-muted hover:text-strong transition-colors"
                onClick={() => {
                  setPreviewImageUrl(null);
                  setPreviewRawUrl(null);
                  setPreviewError(false);
                }}
              >
                Close
              </button>
            </div>

            <div className="flex flex-col items-center gap-3">
              <img
                src={previewImageUrl}
                alt="Item"
                className="max-h-[70vh] object-contain rounded-md"
                onLoad={() => setPreviewError(false)}
                onError={() => setPreviewError(true)}
              />

              {previewError && externalUrl && (
                <div className="text-xs text-muted text-center mt-2">
                  If preview doesn&apos;t load,{" "}
                  <a
                    href={externalUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-brand-600 dark:text-brand-400 hover:underline"
                  >
                    Open image in new tab
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit item modal */}
      {showEditModal && editItem && (
        <div className="fixed inset-0 z-40 flex items-start justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-fade-in overflow-y-auto">
          <div className="glass-card w-full max-w-lg p-6 animate-scale-in my-auto">
            <h2 className="text-sm font-semibold text-strong mb-4">
              Edit item{" "}
              <span className="font-mono text-brand-600 dark:text-brand-400">
                {editItem.code} – {editItem.name}
              </span>
            </h2>

            <form
              onSubmit={handleUpdateItem}
              className="grid gap-3 md:grid-cols-2"
            >
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-muted mb-1.5">
                  Item Code*
                </label>
                <input
                  type="text"
                  value={editItem.code}
                  onChange={(e) =>
                    setEditItem((prev) => ({ ...(prev || {}), code: e.target.value }))
                  }
                  className="glass-input"
                />
              </div>

              {/* Name */}
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-muted mb-1.5">
                  Item Name *
                </label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="glass-input"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-xs font-medium text-muted mb-1.5">
                  Category *
                </label>
                <CustomSelect
                  value={editCategoryId}
                  onChange={setEditCategoryId}
                  placeholder={loadingCategories ? "Loading categories..." : "Select category"}
                  options={categories.map((cat) => ({ value: String(cat.id), label: cat.name }))}
                />
              </div>

              {/* Location */}
              <div>
                <label className="block text-xs font-medium text-muted mb-1.5">
                  Location (Gudang) *
                </label>
                <CustomSelect
                  value={editLocationId}
                  onChange={setEditLocationId}
                  placeholder={loadingLocations ? "Loading locations..." : "Select location"}
                  options={locations.map((loc) => ({ value: String(loc.id), label: loc.name }))}
                />
              </div>

              {/* Unit */}
              <div>
                <label className="block text-xs font-medium text-muted mb-1.5">
                  Unit
                </label>
                <input
                  type="text"
                  value={editUnit}
                  onChange={(e) => setEditUnit(e.target.value)}
                  className="glass-input"
                />
              </div>

              {/* Posisi */}
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-muted mb-1.5">
                  Posisi
                </label>
                <input
                  type="text"
                  value={editPosisi}
                  onChange={(e) => setEditPosisi(e.target.value)}
                  className="glass-input"
                  placeholder="e.g. Rak A1 / Zona 3"
                />
              </div>

              {/* Minimum Stock */}
              <div>
                <label className="block text-xs font-medium text-muted mb-1.5">
                  Minimum Stok
                </label>
                <input
                  type="number"
                  min={0}
                  value={editMinimumStock}
                  onChange={(e) => setEditMinimumStock(e.target.value)}
                  className="glass-input"
                  placeholder="0 = tidak ada minimum"
                />
                <p className="text-[10px] text-muted mt-1">Set 0 untuk menonaktifkan peringatan stok.</p>
              </div>

              {/* High Priority */}
              <div>
                <label className="block text-xs font-medium text-muted mb-1.5">
                  Dashboard
                </label>
                <label className="flex items-center gap-2 text-xs cursor-pointer select-none mt-2">
                  <input
                    type="checkbox"
                    checked={editHighPriority}
                    onChange={(e) => setEditHighPriority(e.target.checked)}
                    className="h-4 w-4 rounded border-white/30"
                  />
                  Barang High Priority
                </label>
                <p className="text-[10px] text-muted mt-1">Tampilkan barang ini di panel "Barang High Priority" pada Dashboard Admin/Super Admin.</p>
              </div>

              {/* Foto Barang (upload) */}
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-muted mb-1.5">
                  Foto Barang
                </label>

                {/* Current photo or preview */}
                {(editPhotoPreview || editCurrentPhotoUrl) && (
                  <div className="mb-3 flex items-start gap-3">
                    <img
                      src={editPhotoPreview || editCurrentPhotoUrl}
                      alt="Foto barang"
                      className="w-24 h-24 object-cover rounded-xl border border-white/20"
                    />
                    <div className="flex flex-col gap-1.5 mt-1">
                      {editPhotoPreview ? (
                        <>
                          <button
                            type="button"
                            onClick={handleUploadPhoto}
                            disabled={editPhotoUploading}
                            className="btn-primary !px-3 !py-1.5 !text-xs"
                          >
                            {editPhotoUploading ? "Uploading..." : "Simpan Foto"}
                          </button>
                          <button
                            type="button"
                            onClick={() => { setEditPhotoFile(null); setEditPhotoPreview(null); if (editPhotoInputRef.current) editPhotoInputRef.current.value = ""; }}
                            className="btn-ghost !px-3 !py-1.5 !text-xs"
                          >
                            Batal
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={handleRemovePhoto}
                          className="px-3 py-1.5 text-xs rounded-md border border-red-400 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10"
                        >
                          Hapus foto
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Upload / camera buttons */}
                <div className="flex gap-2 flex-wrap">
                  <label className="btn-outline !px-3 !py-1.5 !text-xs cursor-pointer">
                    <input
                      ref={editPhotoInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleEditPhotoChange}
                    />
                    Upload Foto
                  </label>
                  <label className="btn-outline !px-3 !py-1.5 !text-xs cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={handleEditPhotoChange}
                    />
                    Ambil Foto (Kamera)
                  </label>
                </div>
                <p className="mt-1.5 text-[11px] text-muted">
                  Upload langsung atau foto dari kamera HP. Format: JPG, PNG, WEBP.
                </p>
              </div>

              {/* Image URL (Google Drive) */}
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-muted mb-1.5">
                  Image URL (Google Drive)
                </label>
                <input
                  type="text"
                  value={editImageUrl}
                  onChange={(e) => setEditImageUrl(e.target.value)}
                  className="glass-input"
                  placeholder="https://..."
                />
                <p className="mt-1 text-[11px] text-muted">
                  Optional. Paste link Google Drive atau URL gambar langsung.
                </p>
              </div>

              <div className="md:col-span-2 mt-1">
                {editError && (
                  <div className="text-xs text-red-600 mb-2">{editError}</div>
                )}
                <div className="flex justify-between items-center gap-2">

                  {/* LEFT SIDE — HIDE or RESTORE */}
                  <div>
                    {editItem?.is_active !== false ? (
                      <button
                        type="button"
                        onClick={handleHideItem}
                        className="px-3 py-1.5 text-xs rounded-md border border-red-500 text-red-600 bg-white/80 dark:bg-white/5 hover:bg-red-50"
                      >
                        Hide item
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleRestoreItem}
                        className="px-3 py-1.5 text-xs rounded-md border border-green-500 text-green-600 bg-white/80 dark:bg-white/5 hover:bg-green-50"
                      >
                        Restore item
                      </button>
                    )}
                  </div>

                  {/* RIGHT SIDE — CANCEL & SAVE */}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="btn-ghost !px-3 !py-1.5 !text-xs"
                      onClick={closeEditModal}
                      disabled={editLoading}
                    >
                      Cancel
                    </button>

                    <button
                      type="submit"
                      disabled={editLoading}
                      className="btn-primary !px-4 !py-1.5 !text-xs"
                    >
                      {editLoading ? "Saving…" : "Save"}
                    </button>
                  </div>

                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* History modal */}
      {showHistoryModal && historyItem && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-fade-in">
          <div className="glass-card w-full max-w-2xl p-6 animate-scale-in max-h-[85vh] flex flex-col">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h2 className="text-sm font-semibold text-strong">Riwayat Barang</h2>
                <p className="text-xs text-muted mt-0.5">
                  <span className="font-mono text-brand-600 dark:text-brand-400">{historyItem.code}</span> — {historyItem.name}
                </p>
              </div>
              <button
                type="button"
                onClick={closeHistoryModal}
                className="text-muted hover:text-strong leading-none text-lg"
              >
                ×
              </button>
            </div>

            <div className="overflow-y-auto flex-1 -mx-1 px-1">
              {historyLoading && (
                <p className="text-xs text-muted py-6 text-center">Memuat riwayat…</p>
              )}
              {!historyLoading && historyError && (
                <p className="text-xs text-red-500 py-6 text-center">{historyError}</p>
              )}
              {!historyLoading && !historyError && historyEntries.length === 0 && (
                <p className="text-xs text-muted py-6 text-center">Belum ada aktivitas tercatat untuk barang ini.</p>
              )}
              {!historyLoading && !historyError && historyEntries.length > 0 && (
                <ul className="space-y-2">
                  {historyEntries.map((entry, idx) => (
                    <li key={idx} className="glass-panel px-3 py-2 rounded-xl text-xs">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-strong">{entry.action_label}</span>
                        <span className="text-muted">
                          {entry.timestamp ? new Date(entry.timestamp).toLocaleString("id-ID") : "-"}
                        </span>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-muted">
                        {entry.user && <span>User: {entry.user}</span>}
                        {entry.quantity !== null && entry.quantity !== undefined && (
                          <span>Qty: {entry.quantity}</span>
                        )}
                        {entry.status && <span>Status: {entry.status}</span>}
                      </div>
                      {entry.detail && (
                        <div className="mt-1 text-muted">{entry.detail}</div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                className="btn-ghost !px-3 !py-1.5 !text-xs"
                onClick={closeHistoryModal}
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-fade-in">
          <div className="glass-card w-full max-w-md p-6 animate-scale-in">
            <h2 className="text-sm font-semibold text-strong mb-3">Remove item</h2>
            <p className="text-xs text-muted mb-3">
              This will remove the selected item from the Items list. Existing
              transactions and history will not be deleted.
            </p>

            <label className="block text-xs font-medium text-muted mb-1.5">
              Select item
            </label>
            <CustomSelect
              value={deleteItemId}
              onChange={setDeleteItemId}
              placeholder="Choose an item…"
              className="mb-2"
              options={items.map((item) => ({ value: String(item.id), label: `${item.code} — ${item.name}` }))}
            />

            {deleteError && (
              <div className="text-xs text-red-600 mb-2">{deleteError}</div>
            )}

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="btn-ghost !px-3 !py-1.5 !text-xs"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteItemId("");
                  setDeleteError("");
                }}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deleteLoading}
                className="px-3 py-1.5 text-xs rounded-md bg-red-500 text-white font-semibold hover:bg-red-600 disabled:opacity-60"
                onClick={handleConfirmDelete}
              >
                {deleteLoading ? "Removing…" : "Remove item"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ItemsList;
